import { config } from 'src/config';
import { getLoginEnvInfo } from 'src/utils/platform';
import packageJson from '../package.json';

export const APP_ID = 'casr_web_app';
export const API_KEY = '1edcc6df-c65e-4bd6-95bc-a1be93487385';
// Single source of truth: bump the app version in package.json.
export const APP_BUILD = packageJson.version;
export const APP_NAME = 'web_app';
export const LOGIN_EXTRA_INFO = {
  _extra: {
    app_version: APP_BUILD,
    ...getLoginEnvInfo(),
  },
};

export const THEMES = {
  LIGHT: 'LIGHT',
};

// Local (on-device) recognition is switched off product-wide, so the app always
// falls back to server recognition. Flip to true to bring it back.
export const LOCAL_RECOGNITION_ENABLED = false;

export const DICTATION_PROVIDER = 'dictation';

export const DICTATION_SCENARIOS = {
  long: 'dictation_casr',
  medium: 'dictation_casr_medium',
  short: 'dictation_casr_simple',
  simple: 'dictation_casr_child_up_to_3_grade',
};

export const DICTATION_SCENARIO_DIFFICULTY_ORDER = [
  DICTATION_SCENARIOS.long,
  DICTATION_SCENARIOS.medium,
  DICTATION_SCENARIOS.simple,
  DICTATION_SCENARIOS.short,
];

export const MIN_REGISTRATION_AGE = 16;
export const ADULT_AGE = 18;
export const TERMS_OF_SERVICE_LINK =
  'https://www.voiceitt.com/legal/terms-of-service';
export const PRIVACY_POLICY_LINK = 'https://www.voiceitt.com/legal/privacy';
export const OPENAI_TERMS_LINK = 'https://openai.com/policies/terms-of-use';
export const OPENAI_PRIVACY_LINK = 'https://openai.com/policies/privacy-policy';
export const FAQ_LINK = 'https://www.voiceitt.com/faq#Using_Voiceitt';
export const IP_API_LINK = 'https://ipapi.co/json/';

export const INIT_CASR_ERROR = {
  no_model: 'no_model',
};

export const RECOGNITION_ERROR_CODES = {
  FAILED_TO_LOGIN: 1001,
  ALREADY_STREAMING: 1011,
  // The recognition service allows one live session per user. Sent to the
  // client that arrives second, while another one already holds the session.
  SESSION_HELD_ELSEWHERE: 1012,
  CONSENTS_REQUIRED: 1013,
};

export const SUPPORT_EMAIL = 'support@voiceitt.com';
export const PARENTAL_CONSENT_FORM_LINKS = {
  USA: 'https://eform.pandadoc.com/?eform=71a2610f-dd98-4f1d-a361-b33b8776d6dd&utm_source=hs_email&utm_medium=email&_hsenc=p2ANqtz-_UaYPl2PQQOhNQLVzB0iS8vY6UQjMnhXEnzv6RVrAkbT1FtmibvDpOK5-Bx6adT6-M7b7b',
  CAN: 'https://eform.pandadoc.com/?eform=71a2610f-dd98-4f1d-a361-b33b8776d6dd&utm_source=hs_email&utm_medium=email&_hsenc=p2ANqtz-_UaYPl2PQQOhNQLVzB0iS8vY6UQjMnhXEnzv6RVrAkbT1FtmibvDpOK5-Bx6adT6-M7b7b',
  AUS: 'https://eform.pandadoc.com/?eform=d2237a45-a80c-4e6a-85a3-f579a3bf7e9f&utm_source=hs_email&utm_medium=email&_hsenc=p2ANqtz-_HUaf1fuIL6ZXoX_v31xCyQNdCZ4V6_VQ6wM8BlspvKbyank28OA7yuSWgfpygl82Dd3fV',
  OTHER:
    'https://6631765.fs1.hubspotusercontent-na1.net/hubfs/6631765/Voiceitt%20-%20Parental%20Consent%20Form%20.pdf?utm_source=hs_email&utm_medium=email&_hsenc=p2ANqtz-99qMXq6uX_vO0cRXpcKRg-ADML2_xWEB21GGY7lnjVgm4IxPRzTmvCawwfTi5Vvt752zIO',
};

export const RECOGNITION_MODE = {
  CONVERSATION: 'conversation',
  DICTATION: 'dictation',
  CHAT_WITH_ASSISTANT: 'chat_with_assistant',
  MEETING_WEBEX: 'meeting_webex',
};

export const NAVIGATION_PAGE = {
  RECORD: 'record',
  SPEAK: 'speak',
  DICTATE: 'dictate',
};

export const COOKIES_CONSENT_TYPES = {
  ESSENTIAL: 'essential',
  NON_ESSENTIAL: 'non_essential',
};

export const DEFAULT_COOKIES_CONSENT_TYPES = [COOKIES_CONSENT_TYPES.ESSENTIAL];

export const RECOGNITION_LANGUAGE = 'en-US';
export const OUTPUT_LANGUAGE = 'en-US';

export const AUDIO_SAMPLE_RATE = 16000;
export const AUDIO_CHANNELS_COUNT = 1;
export const AUDIO_BITS_PER_SAMPLE = 16;

export const WAVE_FORMAT = {
  PCM: 1,
  IEEE_FLOAT: 3,
};

export const PHRASES_SOURCES = {
  STATIC: 'static',
  DATASET: 'dataset',
  EDITED: 'edited',
  CUSTOM: 'custom',
};

export const DEFAULT_DATASET_TRAIN_SET_PHRASES_COUNT = 100;

export const TRAIN_SET_VARIANTS = {
  CUSTOM_PHRASES: 'custom',
  DATASET_PHRASES: 'dataset',
};

export const AUDIO_LIMITS = {
  IGNORE_DURATION: 0.15,
  MIN_DURATION: 1,
  MAX_DURATION: 8,
  MAX_DURATION_BACKEND: 20,
  CHAR_DURATION: 0.04,
  MIN_VAD_DURATION: 0.3,
  // How long a take must have run before silence is allowed to end it. A
  // detector that mis-hears the first moments -- calibration catching the voice
  // instead of the room, a device too slow to feed it in time -- otherwise ends
  // the recording a second or two in, and validation then tells the user their
  // audio was "too short". Losing the take is the expensive part, so silence
  // only ends a take that has had a chance to be a take; the user's own tap and
  // MAX_DURATION still end it whenever they say so.
  MIN_DURATION_BEFORE_VAD_STOP: 3,
};

// Where the start-of-recording cue sits in the flow. `AFTER_MIC_OPEN` plays the
// beep once the recorder is really capturing, so the cue no longer arrives
// while the microphone is still being opened and the first words spoken after
// it are lost. `BEFORE_MIC_OPEN` is the previous order, kept so a flow can be
// rolled back on its own.
export const BEEP_TIMING = {
  BEFORE_MIC_OPEN: 'before_mic_open',
  AFTER_MIC_OPEN: 'after_mic_open',
};

// Rollback switches: set either one back to BEEP_TIMING.BEFORE_MIC_OPEN to
// restore the old order for that flow without touching the other.
export const RECOGNITION_BEEP_TIMING = BEEP_TIMING.AFTER_MIC_OPEN;
export const TRAINING_BEEP_TIMING = BEEP_TIMING.AFTER_MIC_OPEN;

export const SETTINGS_VOICE_PATH = 'app.web.outputVoiceIdentifier';
export const SETTINGS_INSTALL_PROMPT_CLOSED_PATH =
  'app.web.install_prompt_closed';
export const SETTINGS_MIN_SILENCE_PATH = 'min_silence_length_seconds';
export const SETTINGS_FILTER_PROFANITIES_PATH = 'app.filter_profanities';
export const SETTINGS_FORCE_PROFANITY_FILTER_PATH =
  'app.force_profanity_filter';
export const SETTINGS_CHAT_GPT_ENABLED_PATH = 'app.chat_gpt_enabled';
export const SETTINGS_DICTATION_SCENARIO_PATH = 'app.dictation_scenario';
export const SETTINGS_DISCRETE_ENABLED_PATH = 'app.discrete_enabled';

// Whether the server lets this user turn Private mode on at all. Absent or
// falsy means the client must not send `save_audio` in either direction.
export const SETTINGS_PRIVATE_MODE_ALLOWED_PATH = 'app.no_audio_save_allowed';

export const PRIVATE_MODE_DURATION_MS = 60 * 60 * 1000;

export const DICTATION_INACTIVITY_FAIL_COUNT = 5;

// Recognition socket lifecycle. The socket is only opened while a screen that
// can record holds it, and is dropped again once nobody needs it, so an idle
// user does not occupy a model slot on the recognition server.
// Grace period after the last screen releases the socket. Covers navigation
// between recognition screens, where the old one unmounts before the new one
// mounts.
export const RECOGNITION_RELEASE_GRACE_MS = 10 * 1000;
// How long a held but unused connection stays open before it is dropped.
// Pressing record reconnects on demand.
export const RECOGNITION_IDLE_DISCONNECT_MS = 5 * 60 * 1000;
// Upper bound for the on-demand connect triggered by pressing record.
export const RECOGNITION_CONNECT_TIMEOUT_MS = 60 * 1000;
// A socket that never reports `connection_ready` cannot stream but still holds
// a server slot, so it is dropped rather than left hanging.
export const RECOGNITION_READY_TIMEOUT_MS = 90 * 1000;
export const RECOGNITION_RECONNECT_BASE_DELAY_MS = 2 * 1000;
export const RECOGNITION_RECONNECT_MAX_DELAY_MS = 60 * 1000;
export const RECOGNITION_MAX_RECONNECT_ATTEMPTS = 6;

export const TRAINING_STATE = {
  no_records: 1,
  record_in_progress: 2,
  waiting_for_model: 3,
  model_built: 4,
};

export const MISSING_MODEL_ISSUE = {
  INCOMPLETE_TRAINING: 1,
};

export const ASSISTANT_CHAT_ROLES = {
  USER: 'user',
  BOT: 'bot',
};
